import React, { useState, useEffect, useCallback } from 'react';
import { CalendarClock, Video, Phone, RefreshCw, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Front-and-centre widget on the Platform Dashboard so the admin instantly sees
// which lead booked a demo and when — without hunting through CRM tabs.
export default function UpcomingDemosWidget({ token }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ upcoming: [], upcoming_count: 0, today_count: 0 });
  const [loading, setLoading] = useState(true);

  const fetchDemos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/demos`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setData(d);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  return (
    <div className="bg-white rounded-2xl border border-orange-200 shadow-sm overflow-hidden" data-testid="dashboard-upcoming-demos">
      <div className="px-5 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5" />
          <div>
            <p className="text-sm font-bold leading-tight">🎬 आने वाली Demos</p>
            <p className="text-[11px] opacity-90">{data.today_count} आज · {data.upcoming_count} कुल upcoming</p>
          </div>
        </div>
        <button onClick={fetchDemos} disabled={loading} data-testid="dashboard-demos-refresh"
          className="p-1.5 rounded-lg hover:bg-white/20 transition disabled:opacity-50" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="divide-y divide-zinc-100">
        {loading && data.upcoming.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-6">लोड हो रहा है…</p>
        ) : data.upcoming.length === 0 ? (
          <div className="text-center py-6 px-4" data-testid="dashboard-demos-empty">
            <p className="text-sm text-zinc-500">अभी कोई आने वाली demo book नहीं हुई।</p>
            <p className="text-[11px] text-zinc-400 mt-1">जैसे ही कोई lead demo book करेगी, वो यहाँ और Lead CRM → Demos में दिखेगी।</p>
          </div>
        ) : (
          data.upcoming.slice(0, 6).map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-orange-50/40 transition-colors" data-testid={`dashboard-demo-${d.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-900 truncate">{d.name}{d.school_name ? <span className="text-zinc-400 font-normal"> · {d.school_name}</span> : null}</p>
                <p className="text-xs text-zinc-500 tabular-nums flex items-center gap-1"><Phone className="w-3 h-3" /> {d.phone || '—'}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-orange-600 tabular-nums flex items-center justify-end gap-1"><CalendarClock className="w-3.5 h-3.5" /> {d.when_label}</p>
                {d.meet_link && (
                  <a href={d.meet_link} target="_blank" rel="noopener noreferrer" data-testid={`dashboard-demo-meet-${d.id}`}
                    className="mt-1 text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
                    <Video className="w-3 h-3" /> Join
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <button onClick={() => navigate('/lead-crm')} data-testid="dashboard-demos-view-all"
        className="w-full px-5 py-2.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 border-t border-orange-100 inline-flex items-center justify-center gap-1 transition">
        सभी Demos देखें (Lead CRM → 🎬 Demos) <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
